import { Fragment, useRef } from 'react';
import { scaleBand } from 'd3-scale';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { List } from '@mentimeter/ragnar-ui/list';
import { VisualizationTextMarkupRenderer } from '@mentimeter/tiptap/visualization-text-markup-renderer';
import { Legend } from '../components/Legend';
import { TextFitGhost } from '../components/text-fit-ghost/TextFitGhost';
import { VISUALIZATION_LABEL_FONTSIZES } from '../utils/constants';
import { getLongestLabel } from '../utils/get-longest-label';
import { LegendDot } from './LegendDot';

interface LegendItem {
  id: string;
  label: string;
  dotNumber: string;
  color: string;
}
interface RatingGridLegendProps extends BoxT {
  items: LegendItem[];
  onItemHover: (itemId: LegendItem['id'] | null) => void;
  itemKeyAccessor: (d: LegendItem) => string;
  maxHeight: number;
  minWidth: number;
  hoveredItemKey: string | null;
}

export const RatingGridLegend = ({
  items,
  itemKeyAccessor,
  onItemHover,
  hoveredItemKey,
  maxHeight,
  maxWidth,
  minWidth,
}: RatingGridLegendProps) => {
  const labelsContainerRef = useRef<HTMLDivElement | null>(null);

  const itemYScale = scaleBand()
    .domain(items.map(itemKeyAccessor).map(String))
    .range([0, maxHeight]);
  const legendItemHeight = itemYScale.bandwidth();
  const longestLabelId = getLongestLabel(
    items,
    (d) => d.label,
    (d) => d.id ?? '',
  );

  const hasLabelsToResize = longestLabelId !== null;

  return (
    <List
      maxWidth={maxWidth}
      minWidth={minWidth}
      justifyContent="center"
      maxHeight={maxHeight}
      height={maxHeight}
      ref={labelsContainerRef}
      gap="space1"
      style={{
        opacity: hasLabelsToResize ? 0 : 1,
        transition: 'opacity 0.5s ease',
      }}
    >
      {items.map((item, index) => {
        const isLongestLabel = item.id === longestLabelId;
        const opacity =
          hoveredItemKey === item.id || hoveredItemKey === null ? 1 : 0.2;

        const verticalPadding = Math.min(legendItemHeight * 0.1, 12);

        return (
          <Fragment key={item.id}>
            {index !== 0 && <Legend.ItemDivider />}
            <Legend.Item
              opacity={opacity}
              onMouseEnter={() => onItemHover(itemKeyAccessor(item))}
              onMouseLeave={() => onItemHover(null)}
              alignItems="center"
              py={verticalPadding}
              width="unset"
              maxHeight={legendItemHeight}
            >
              <LegendDot
                dotNumber={item.dotNumber}
                color={item.color}
                maxHeight={legendItemHeight}
              />
              <Box
                alignItems="flex-start"
                justifyContent="flex-start"
                style={{
                  flexShrink: 1, // Prevents the legend item from overflowing the container
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              >
                {isLongestLabel && (
                  <TextFitGhost
                    // not the real width, used only to retrigger the font size calculation
                    width={maxWidth}
                    height={legendItemHeight - verticalPadding * 2}
                    text={item.label}
                    finalFontSizeContainerRef={labelsContainerRef}
                    fontSizes={VISUALIZATION_LABEL_FONTSIZES}
                  >
                    {(ref) => (
                      <Text
                        aria-hidden="true"
                        ref={ref}
                        style={{ wordBreak: 'break-word' }}
                      >
                        {item.label}
                      </Text>
                    )}
                  </TextFitGhost>
                )}
                <Text
                  fontSize="inherit"
                  lineHeight="normal"
                  style={{ wordBreak: 'break-word', overflow: 'hidden' }}
                >
                  <VisualizationTextMarkupRenderer content={item.label} />
                </Text>
              </Box>
            </Legend.Item>
          </Fragment>
        );
      })}
    </List>
  );
};
